import { useTranslation } from 'react-i18next';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';

import Iconify from '@/components/media/iconify';
import { useCopyToClipboard } from '@/hooks/ui/use-copy-to-clipboard';
import { useSnackbar } from '@/components/feedback/snackbar';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface OdooStatusBadgeProps {
  /** The Odoo external ID. Undefined or null means not linked. */
  externalId?: number | null;
  /** Size variant for the chip. */
  size?: 'small' | 'medium';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OdooStatusBadge({ externalId, size = 'small' }: OdooStatusBadgeProps) {
  const { t } = useTranslation();
  const { copy } = useCopyToClipboard();
  const { enqueueSnackbar } = useSnackbar();

  const isLinked = externalId != null;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isLinked) return;
    const success = await copy(String(externalId));
    if (success) {
      enqueueSnackbar(t('Odoo ID copied to clipboard'), { variant: 'success' });
    }
  };

  if (!isLinked) {
    return (
      <Chip
        size={size}
        label={t('Not linked with Odoo')}
        color="default"
        variant="outlined"
        icon={<Iconify icon="mdi:link-off" width={16} />}
      />
    );
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Chip
        size={size}
        label={`Odoo ID: ${externalId}`}
        color="success"
        variant="outlined"
        icon={<Iconify icon="mdi:link" width={16} />}
      />
      <Tooltip title={t('Copy Odoo ID')} arrow>
        <IconButton size="small" onClick={handleCopy} sx={{ p: '2px' }}>
          <Iconify icon="solar:copy-bold-duotone" width={16} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
